import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createVulnerability, resetState, setCreateVulnError } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import {
  selectNameField,
  selectDescriptionField,
  selectSeverityField,
  selectTargetsField,
  selectIsWebVuln,
  selectWorkspacesField,
  selectFieldsError
} from 'store/ManageEditCreate/selectors';
import isEmpty from 'lodash/isEmpty';
import { PropTypes } from 'prop-types';
import useFormShortcuts from 'Hooks/useFormShortcuts';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import {
  Wrapper, Title, ButtonWrapper, CancelButton, CreateButton, NextIcon
} from './styled';
import { selectCurrentHost } from 'store/Host/selectors';
import { createVulnFromAsset } from 'store/Contextualization/AssetDetail/actions';
import { useIntl } from 'react-intl';
import { createVulnOutsideWs } from 'store/ManageGeneral/actions';
import { selectLocation } from 'store/Router/selectors';
import { selectCurrentWorkspace, selectIsReadOnly } from 'store/Faraday/selectors';

const Header = ({ handleClose, setActiveTab, entity, assets, scope }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const readonly = useSelector(selectIsReadOnly);
  const canUpdate = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));
  const canSubmit = !readonly && canUpdate;

  const location = useSelector(selectLocation);
  const currentWs = useSelector(selectCurrentWorkspace);
  const isOutsideWorkspace = location.pathname.startsWith('/vulnerabilities') && !location.pathname.includes(`/manage/${currentWs}`);

  const name = useSelector(selectNameField);
  const description = useSelector(selectDescriptionField);
  const severity = useSelector(selectSeverityField);
  const targets = useSelector(selectTargetsField);
  const isWebVuln = useSelector(selectIsWebVuln);
  const hasTargets = !isEmpty(targets);
  const assetForWorking = useSelector(selectCurrentHost);
  const createFromAsset = Object.keys(assetForWorking).length > 0;
  const servicesTargets = isOutsideWorkspace ? targets.filter((target) => target?.value?.type === 'Service') : targets.filter((target) => target?.type === 'Service');
  const hasServicesTargets = !isEmpty(servicesTargets);
  const errorMessage = intl.formatMessage({ id: 'modal.createVuln.cantCreate.message' });
  const errorTitle = intl.formatMessage({ id: 'modal.createVuln.cantCreate.title' });
  const workspaces = useSelector(selectWorkspacesField);
  const error = useSelector(selectFieldsError);
  const values = { workspaces, name, description, severity, assets };

  const validTargets = () => {
    if (isWebVuln) return hasServicesTargets;
    return hasTargets;
  };

  const onHandleClose = () => {
    handleClose();
    setActiveTab('general');
    dispatch(resetState());
  };

  const handleCreateVuln = () => {
    if (validTargets()) {
      if (createFromAsset) dispatch(createVulnFromAsset());
      else if (entity === 'vulnsGeneral') dispatch(createVulnOutsideWs());
      else dispatch(createVulnerability(entity));
      if (hasTargets) onHandleClose();
    }
  };

  const text = 'Create';

  const entityFields = {
    vulnsGeneral: ['workspaces', 'name', 'description', 'severity', 'assets'],
    vulnsAssets: ['name', 'description', 'severity'],
    vulns: ['name', 'description', 'severity', 'assets'],
    default: ['name', 'description', 'severity']
  };

  const fieldsToCheck = entityFields[entity] || entityFields.default;

  const mandatoryFieldsFilled = fieldsToCheck.every(
    field => !isEmpty(values[field])
  );

  const showErrorModal = () => dispatch(setCreateVulnError(true, errorMessage, errorTitle));
  const handleSave = mandatoryFieldsFilled ? handleCreateVuln : showErrorModal;

  useFormShortcuts({
    onSave: canSubmit ? handleSave : undefined,
    onCancel: onHandleClose,
    canSave: !error,
    saveLabel: text,
    scope
  });

  return (
    <Wrapper>
      <Title children="Create New Vulnerability" />
      <ButtonWrapper>
        <CancelButton onClick={ onHandleClose } children="Cancel" />
        <CreateButton
          disabled={ error }
          onClick={ handleSave }
        >
          {/* <span children={ text } /> */}
          <>{text}</>
          <NextIcon />
        </CreateButton>
      </ButtonWrapper>
    </Wrapper>
  );
};

Header.propTypes = {
  handleClose: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired
};

export default Header;
